import { Component, Input, OnChanges } from '@angular/core';
import { environment } from '@environments/environment';
import { NumberFormatOptions } from '@progress/kendo-angular-intl';

@Component({
    selector: 'app-number',
    template: `
        @if (isDecimalFormat) {
            <span>{{ value | number: numberFormat }}</span>
        } @else if (isPercentFormat) {
            @if (value) {
                <span>{{ value | number: '1.2-2' }} %</span>
            }
        } @else if (format === '#') {
            <span>{{ value }}</span>
        } @else if (format?.currency) {
            <span>
                {{ value | currency: format.currency : currencyFormat : currencyNumberFormat }}
            </span>
        } @else if (currency) {
            <span>
                {{ value | currency: environmentCurrencyFormat.currency : envCurrencyFormat : envCurrencyNumberFormat }}
            </span>
        } @else if (isStringFormat) {
            <span>{{ value | number: format }}</span>
        } @else {
            <span>{{ value | number }}</span>
        }
    `
})
export class NumberComponent implements OnChanges {
    @Input() value: number;
    @Input() format: string | NumberFormatOptions | any;
    @Input() currency: string;

    protected environmentCurrencyFormat: NumberFormatOptions;

    ngOnChanges(): void {
        this.environmentCurrencyFormat = environment.settings.appControl.currencyFormat({ currency: this.currency });
    }

    /**
     * Checks if the format is a decimal format.
     * Example: 'n3' -> true
     * Example: '1.0-5' -> false
     */
    protected get isDecimalFormat(): boolean {
        return typeof this.format === 'string' && this.format.startsWith('n') && /^n\d+$/.test(this.format);
    }

    /**
     * Checks if the format is a percent format.
     * Example: 'p3' -> true
     * Example: '1.0-5' -> false
     */
    protected get isPercentFormat(): boolean {
        return typeof this.format === 'string' && this.format === 'percent';
    }

    /**
     * Checks if the format is a string.
     */
    protected get isStringFormat(): boolean {
        return typeof this.format === 'string';
    }

    protected get numberFormat(): string {
        if (!this.isDecimalFormat && !this.isPercentFormat) return '';
        const decimals = parseInt(this.format.substring(1), 10);
        return `1.${decimals}-${decimals}`;
    }

    protected get currencyFormat(): string {
        return `${this.format.currencyDisplay}-narrow`;
    }

    protected get currencyNumberFormat(): string {
        return `1.${this.format.minimumFractionDigits}-${this.format.maximumFractionDigits}`;
    }

    protected get envCurrencyFormat(): string {
        return `${this.environmentCurrencyFormat.currencyDisplay}-narrow`;
    }

    protected get envCurrencyNumberFormat(): string {
        return `1.${this.environmentCurrencyFormat.minimumFractionDigits}-${this.environmentCurrencyFormat.maximumFractionDigits}`;
    }
}
